import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Calendar, 
  FileText, 
  Users, 
  CheckSquare, 
  Download, 
  Shield,
  Clock,
  Star,
  Mail,
  Lock,
  BarChart3,
  UserCheck,
  Lightbulb,
  ArrowRight,
  RefreshCw
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Création de PV',
      description: 'Créez facilement vos procès-verbaux avec un formulaire structuré et une mise en forme automatique.',
      highlight: false
    },
    {
      icon: <Lightbulb className="w-6 h-6" />,
      title: 'Collecte des propositions',
      description: 'Permettez aux membres de proposer des points à l\'ordre du jour avant le comité. Intégration automatique dans le PV.',
      highlight: false
    },
    {
      icon: <RefreshCw className="w-6 h-6" />,
      title: 'Continuité automatique',
      description: 'Les points et tâches non terminés sont automatiquement repris dans le prochain comité.',
      highlight: false
    },
    {
      icon: <CheckSquare className="w-6 h-6" />,
      title: 'Suivi des tâches',
      description: 'Assignez des tâches avec délais et responsables. Suivez leur progression en temps réel.',
      highlight: false
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Gestion des membres',
      description: 'Gérez votre liste de membres avec coordonnées et présences aux réunions.',
      highlight: false
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Envoi automatique',
      description: 'Envoyez vos PV par email aux membres avec PDF professionnel en pièce jointe.',
      highlight: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-green-100">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">PV Manager</h1>
                <p className="text-sm text-gray-600">Gestion de procès-verbaux</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/login')}
                className="border-green-200 text-green-600 hover:bg-green-50 w-full sm:w-auto"
              >
                Connexion
              </Button>
              <Button 
                onClick={() => navigate('/register')}
                className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
              >
                Créer un compte
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              La solution complète pour vos
              <span className="text-green-600 block">procès-verbaux</span>
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Créez, gérez et partagez vos PV facilement. Collectez les propositions des membres, 
              assurez le suivi des tâches et maintenez la continuité entre vos réunions.
            </p>
            
            {/* Key Benefits */}
            <div className="flex flex-wrap justify-center gap-4 mb-8 text-sm">
              <div className="flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full">
                <Lightbulb className="w-4 h-4" />
                <span>Propositions des membres</span>
              </div>
              <div className="flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full">
                <RefreshCw className="w-4 h-4" />
                <span>Continuité automatique</span>
              </div>
              <div className="flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full">
                <CheckSquare className="w-4 h-4" />
                <span>Suivi des tâches</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
              <Button 
                size="lg"
                onClick={() => navigate('/register')}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 w-full sm:w-auto"
              >
                Commencer gratuitement
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => navigate('/login')}
                className="border-green-200 text-green-600 hover:bg-green-50 px-8 py-3 w-full sm:w-auto"
              >
                Se connecter
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-800 mb-4">
              Fonctionnalités clés
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Des outils pensés pour simplifier vos réunions et assurer un suivi efficace entre les comités.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className={`border transition-all duration-200 hover:shadow-lg ${
                  feature.highlight 
                    ? 'border-green-300 bg-green-50 hover:bg-green-100' 
                    : 'border-gray-200 hover:border-green-200'
                }`}
              >
                <CardHeader>
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                    feature.highlight 
                      ? 'bg-green-200' 
                      : 'bg-green-100'
                  }`}>
                    <div className="text-green-600">
                      {feature.icon}
                    </div>
                  </div>
                  <CardTitle className="text-gray-800 flex items-center gap-2">
                    {feature.title}
                    {feature.highlight && (
                      <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full">
                        Nouveau
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-16 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-800 mb-4">
              Comment ça fonctionne
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Un workflow pensé pour optimiser vos réunions et maintenir la continuité.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                    1
                  </div>
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Collecte</h4>
                <p className="text-sm text-gray-600">
                  Les membres proposent des points à l'ordre du jour via un formulaire simple
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Préparation</h4>
                <p className="text-sm text-gray-600">
                  Le PV se génère automatiquement avec les propositions et points non terminés
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                    3
                  </div>
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Réunion</h4>
                <p className="text-sm text-gray-600">
                  Suivez l'ordre du jour, assignez des tâches et marquez les points traités
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold">
                    4
                  </div>
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Suivi</h4>
                <p className="text-sm text-gray-600">
                  Envoi automatique du PV et report des tâches non terminées au prochain comité
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-600">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-4">
              Prêt à commencer ?
            </h3>
            <p className="text-green-100 mb-8 text-lg">
              Créez votre compte club et commencez à utiliser PV Manager dès maintenant.
            </p>
            <Button 
              size="lg"
              onClick={() => navigate('/register')}
              className="bg-white text-green-600 hover:bg-gray-100 px-8 py-3"
            >
              Créer votre club
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Calendar className="w-5 h-5" />
            <span className="font-semibold">PV Manager</span>
          </div>
          <p className="text-gray-400">
            Simplifiez la gestion de vos procès-verbaux
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;